import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useBills } from '../context/BillContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { formatCurrency } from '../utils/calculations';
import { PlusIcon, TrashIcon, CheckCircleIcon, XCircleIcon, CalendarIcon, BuildingIcon, DollarSignIcon, PencilIcon, FileTextIcon, ArrowRightIcon } from 'lucide-react';
export function BillFlow() {
  const {
    selectedCompany,
    companies
  } = useData();
  const {
    bills: allBills,
    getBillsByCompanyId,
    addBill,
    updateBill,
    deleteBill,
    markBillPaid,
    markBillUnpaid
  } = useBills();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBillId, setEditingBillId] = useState<string | null>(null);
  const [newBill, setNewBill] = useState({
    vendor_name: '',
    description: '',
    total_amount: '',
    due_date: new Date().toISOString().split('T')[0]
  });
  const [allocations, setAllocations] = useState<Array<{
    company_id: string;
    company_name: string;
    amount: string;
  }>>([]);
  const [showVendorSuggestions, setShowVendorSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const vendorInputRef = useRef<HTMLInputElement>(null);
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingBillId, setPayingBillId] = useState<string | null>(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const bills = selectedCompany ? getBillsByCompanyId(selectedCompany.id) : [];
  // Get unique vendor names for autocomplete
  const vendorNames = useMemo(() => {
    const names = new Set<string>();
    allBills.forEach(bill => names.add(bill.vendor_name));
    return Array.from(names).sort();
  }, [allBills]);
  // Filter vendors based on input
  const filteredVendors = useMemo(() => {
    if (!newBill.vendor_name) return vendorNames;
    return vendorNames.filter(name => name.toLowerCase().includes(newBill.vendor_name.toLowerCase()));
  }, [newBill.vendor_name, vendorNames]);
  // Reset selected index when filtered vendors change
  useEffect(() => {
    setSelectedSuggestionIndex(0);
  }, [filteredVendors]);
  const handleVendorKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showVendorSuggestions || filteredVendors.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev < filteredVendors.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredVendors[selectedSuggestionIndex]) {
          setNewBill({
            ...newBill,
            vendor_name: filteredVendors[selectedSuggestionIndex]
          });
          setShowVendorSuggestions(false);
        }
        break;
      case 'Escape':
        setShowVendorSuggestions(false);
        break;
    }
  };
  const handleEditBill = (bill: (typeof bills)[0]) => {
    setEditingBillId(bill.id);
    setNewBill({
      vendor_name: bill.vendor_name,
      description: bill.description,
      total_amount: bill.total_amount.toString(),
      due_date: new Date(bill.due_date).toISOString().split('T')[0]
    });
    setAllocations(bill.allocations.map(a => ({
      company_id: a.company_id,
      company_name: a.company_name,
      amount: a.amount.toString()
    })));
    setShowAddForm(true);
  };
  const handleCancelEdit = () => {
    setEditingBillId(null);
    setNewBill({
      vendor_name: '',
      description: '',
      total_amount: '',
      due_date: new Date().toISOString().split('T')[0]
    });
    setAllocations([]);
    setShowAddForm(false);
  };
  const handleSaveBill = () => {
    if (!newBill.vendor_name || !newBill.total_amount || !selectedCompany) {
      alert('Please fill in vendor name and amount');
      return;
    }
    const totalAmount = parseFloat(newBill.total_amount);
    if (isNaN(totalAmount) || totalAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    // Validate allocations
    const validAllocations = allocations.filter(a => a.company_id && parseFloat(a.amount) > 0);
    const allocatedTotal = validAllocations.reduce((sum, a) => sum + parseFloat(a.amount), 0);
    if (allocatedTotal > totalAmount) {
      alert(`Allocated amount ($${allocatedTotal.toFixed(2)}) exceeds bill total ($${totalAmount.toFixed(2)})`);
      return;
    }
    const billData = {
      vendor_name: newBill.vendor_name,
      description: newBill.description,
      total_amount: totalAmount,
      due_date: new Date(newBill.due_date),
      allocations: validAllocations.map(a => ({
        id: `alloc-${Date.now()}-${Math.random()}`,
        company_id: a.company_id,
        company_name: a.company_name,
        amount: parseFloat(a.amount)
      }))
    };
    if (editingBillId) {
      // Update existing bill
      updateBill(editingBillId, billData);
      // TODO: Handle allocation changes for existing bills
      // For now, we'll just update the main bill
    } else {
      // Add new bill
      const newBillId = `bill-${Date.now()}`;
      addBill({
        id: newBillId,
        vendor_company_id: selectedCompany.id,
        paid: false,
        created_at: new Date(),
        updated_at: new Date(),
        ...billData
      });
      // Create corresponding bills in allocated companies
      validAllocations.forEach(allocation => {
        const allocatedCompany = companies.find(c => c.id === allocation.company_id);
        if (allocatedCompany) {
          addBill({
            id: `bill-${Date.now()}-${Math.random()}`,
            vendor_company_id: allocation.company_id,
            vendor_name: selectedCompany.name,
            description: `${newBill.description ? newBill.description + ' - ' : ''}Allocated from ${newBill.vendor_name}`,
            total_amount: parseFloat(allocation.amount),
            due_date: new Date(newBill.due_date),
            paid: false,
            allocations: [],
            parent_bill_id: newBillId,
            created_at: new Date(),
            updated_at: new Date()
          });
        }
      });
    }
    // Reset form
    handleCancelEdit();
  };
  const handleMarkPaidClick = (billId: string) => {
    setPayingBillId(billId);
    setPaymentNotes('');
    setShowPaymentModal(true);
  };
  const handleConfirmPayment = () => {
    if (payingBillId) {
      markBillPaid(payingBillId, paymentNotes.trim() || undefined);
      setShowPaymentModal(false);
      setPayingBillId(null);
      setPaymentNotes('');
    }
  };
  const handleCancelPayment = () => {
    setShowPaymentModal(false);
    setPayingBillId(null);
    setPaymentNotes('');
  };
  const addAllocationRow = () => {
    setAllocations([...allocations, {
      company_id: '',
      company_name: '',
      amount: ''
    }]);
  };
  const updateAllocationRow = (index: number, field: string, value: string) => {
    const updated = [...allocations];
    if (field === 'company_id') {
      const company = companies.find(c => c.id === value);
      updated[index] = {
        ...updated[index],
        company_id: value,
        company_name: company?.name || ''
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }
    setAllocations(updated);
  };
  const removeAllocationRow = (index: number) => {
    setAllocations(allocations.filter((_, i) => i !== index));
  };
  const getTotalAllocated = (bill: (typeof bills)[0]) => {
    return bill.allocations.reduce((sum, a) => sum + a.amount, 0);
  };
  const getUnallocated = (bill: (typeof bills)[0]) => {
    return bill.total_amount - getTotalAllocated(bill);
  };
  const getTotalDue = () => {
    return bills.filter(b => !b.paid).reduce((sum, b) => sum + b.total_amount, 0);
  };
  const getTotalPaid = () => {
    return bills.filter(b => b.paid).reduce((sum, b) => sum + b.total_amount, 0);
  };
  const isOverdue = (dueDate: Date) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };
  const payingBill = bills.find(b => b.id === payingBillId);
  return <div className="max-w-6xl mx-auto space-y-6" data-id="element-461">
      <div className="flex items-center justify-between" data-id="element-462">
        <div data-id="element-463">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-464">Bill Flow</h1>
          <p className="text-sm text-gray-600" data-id="element-465">
            {bills.length} bills • {bills.filter(b => !b.paid).length} unpaid
          </p>
        </div>
        <Button onClick={() => {
        if (showAddForm && !editingBillId) {
          handleCancelEdit();
        } else {
          setShowAddForm(!showAddForm);
        }
      }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-467" />} data-id="element-466">
          {showAddForm && !editingBillId ? 'Cancel' : 'Add Bill'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-468">
        <Card data-id="element-469">
          <div className="flex items-center justify-between" data-id="element-470">
            <div data-id="element-471">
              <p className="text-sm text-gray-600 mb-1" data-id="element-472">Total Due</p>
              <p className="text-2xl font-bold text-red-600" data-id="element-473">
                {formatCurrency(getTotalDue())}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center" data-id="element-474">
              <XCircleIcon className="w-6 h-6 text-red-600" data-id="element-475" />
            </div>
          </div>
        </Card>

        <Card data-id="element-476">
          <div className="flex items-center justify-between" data-id="element-477">
            <div data-id="element-478">
              <p className="text-sm text-gray-600 mb-1" data-id="element-479">Total Paid</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-480">
                {formatCurrency(getTotalPaid())}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-481">
              <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-482" />
            </div>
          </div>
        </Card>

        <Card data-id="element-483">
          <div className="flex items-center justify-between" data-id="element-484">
            <div data-id="element-485">
              <p className="text-sm text-gray-600 mb-1" data-id="element-486">Total Bills</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-487">{bills.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-488">
              <DollarSignIcon className="w-6 h-6 text-blue-600" data-id="element-489" />
            </div>
          </div>
        </Card>
      </div>

      {/* Payment Confirmation Modal */}
      <Modal isOpen={showPaymentModal} onClose={handleCancelPayment} title="Mark Bill as Paid" data-id="element-490">
        <div className="space-y-4" data-id="element-491">
          {payingBill && <div className="bg-gray-50 rounded-lg p-4" data-id="element-492">
              <div className="flex items-center justify-between mb-2" data-id="element-493">
                <span className="text-sm font-medium text-gray-700" data-id="element-494">
                  Vendor:
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-495">
                  {payingBill.vendor_name}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2" data-id="element-496">
                <span className="text-sm font-medium text-gray-700" data-id="element-497">
                  Amount:
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-498">
                  {formatCurrency(payingBill.total_amount)}
                </span>
              </div>
              {payingBill.description && <div className="flex items-center justify-between" data-id="element-499">
                  <span className="text-sm font-medium text-gray-700" data-id="element-500">
                    Description:
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-501">
                    {payingBill.description}
                  </span>
                </div>}
            </div>}

          <div data-id="element-502">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-503">
              Payment Notes (Optional)
            </label>
            <textarea placeholder="e.g., Check #1234, Wire transfer, Credit card ending in 5678..." value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none" data-id="element-504" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-505">
              Add any relevant payment details like check numbers, transaction
              IDs, or payment methods.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2" data-id="element-506">
            <Button variant="ghost" onClick={handleCancelPayment} data-id="element-507">
              Cancel
            </Button>
            <Button onClick={handleConfirmPayment} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-509" />} data-id="element-508">
              Mark as Paid
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Bill Form */}
      {showAddForm && <Card data-id="element-510">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-511">
            {editingBillId ? 'Edit Bill' : 'Add New Bill'}
          </h2>
          <div className="space-y-4" data-id="element-512">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-513">
              <div className="relative" data-id="element-514">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-515">
                  Vendor Name <span className="text-red-600" data-id="element-516">*</span>
                </label>
                <input ref={vendorInputRef} type="text" placeholder="e.g., AWS, Adobe, Stripe" value={newBill.vendor_name} onChange={e => {
              setNewBill({
                ...newBill,
                vendor_name: e.target.value
              });
              setShowVendorSuggestions(true);
            }} onKeyDown={handleVendorKeyDown} onFocus={() => setShowVendorSuggestions(true)} onBlur={() => setTimeout(() => setShowVendorSuggestions(false), 200)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-517" />
                {/* Vendor Autocomplete Dropdown */}
                {showVendorSuggestions && filteredVendors.length > 0 && newBill.vendor_name && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-id="element-518">
                      {filteredVendors.map((vendor, index) => <button key={index} type="button" onClick={() => {
                setNewBill({
                  ...newBill,
                  vendor_name: vendor
                });
                setShowVendorSuggestions(false);
              }} className={`w-full px-3 py-2 text-sm text-left transition-colors ${index === selectedSuggestionIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-blue-50'}`} data-id="element-519">
                          {vendor}
                        </button>)}
                    </div>}
                {showVendorSuggestions && filteredVendors.length > 0 && <p className="text-xs text-gray-500 mt-1" data-id="element-520">
                    Use ↑↓ arrows to navigate, Enter to select
                  </p>}
              </div>

              <div data-id="element-521">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-522">
                  Total Amount <span className="text-red-600" data-id="element-523">*</span>
                </label>
                <div className="relative" data-id="element-524">
                  <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-525" />
                  <input type="number" step="0.01" placeholder="0.00" value={newBill.total_amount} onChange={e => setNewBill({
                ...newBill,
                total_amount: e.target.value
              })} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-526" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-527">
              <div data-id="element-528">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-529">
                  Description
                </label>
                <input type="text" placeholder="What is this bill for?" value={newBill.description} onChange={e => setNewBill({
              ...newBill,
              description: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-530" />
              </div>

              <div data-id="element-531">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-532">
                  Due Date
                </label>
                <input type="date" value={newBill.due_date} onChange={e => setNewBill({
              ...newBill,
              due_date: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-533" />
              </div>
            </div>

            {/* Allocations */}
            <div data-id="element-534">
              <div className="flex items-center justify-between mb-2" data-id="element-535">
                <label className="block text-sm font-medium text-gray-700" data-id="element-536">
                  Allocate to Companies (Optional)
                </label>
                <Button variant="ghost" size="sm" onClick={addAllocationRow} leftIcon={<PlusIcon className="w-3 h-3" data-id="element-538" />} data-id="element-537">
                  Add Allocation
                </Button>
              </div>

              {allocations.length > 0 && <div className="space-y-3" data-id="element-539">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-540">
                    <div className="flex items-start gap-2" data-id="element-541">
                      <ArrowRightIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-542" />
                      <p className="text-xs text-blue-800" data-id="element-543">
                        <strong data-id="element-544">Note:</strong> Allocating to other companies
                        will create corresponding bills in their profiles with{' '}
                        {selectedCompany?.name} as the vendor.
                      </p>
                    </div>
                  </div>

                  {allocations.map((allocation, index) => <div key={index} className="flex gap-2" data-id="element-545">
                      <select value={allocation.company_id} onChange={e => updateAllocationRow(index, 'company_id', e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-546">
                        <option value="" data-id="element-547">Select Company</option>
                        {companies.filter(c => c.id !== selectedCompany?.id).map(company => <option key={company.id} value={company.id} data-id="element-548">
                              {company.name}
                            </option>)}
                      </select>
                      <div className="relative w-32" data-id="element-549">
                        <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" data-id="element-550" />
                        <input type="number" step="0.01" placeholder="0.00" value={allocation.amount} onChange={e => updateAllocationRow(index, 'amount', e.target.value)} className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-551" />
                      </div>
                      <button onClick={() => removeAllocationRow(index)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" data-id="element-552">
                        <TrashIcon className="w-4 h-4" data-id="element-553" />
                      </button>
                    </div>)}
                </div>}
            </div>

            <div className="flex justify-end gap-2 pt-2" data-id="element-554">
              <Button variant="ghost" onClick={handleCancelEdit} data-id="element-555">
                Cancel
              </Button>
              <Button onClick={handleSaveBill} data-id="element-556">
                {editingBillId ? 'Update Bill' : 'Add Bill'}
              </Button>
            </div>
          </div>
        </Card>}

      {/* Bills List */}
      {bills.length === 0 ? <Card data-id="element-557">
          <div className="text-center py-12" data-id="element-558">
            <DollarSignIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-559" />
            <p className="text-gray-500 mb-4" data-id="element-560">
              No bills yet. Add your first bill above!
            </p>
          </div>
        </Card> : <div className="space-y-3" data-id="element-561">
          {bills.map(bill => {
        const unallocated = getUnallocated(bill);
        const overdue = !bill.paid && isOverdue(bill.due_date);
        const isAllocatedBill = !!bill.parent_bill_id;
        return <Card key={bill.id} padding="none" data-id="element-562">
                <div className="p-4" data-id="element-563">
                  <div className="flex items-start justify-between mb-3" data-id="element-564">
                    <div className="flex-1 min-w-0" data-id="element-565">
                      <div className="flex items-center gap-2 mb-1" data-id="element-566">
                        <h3 className="text-base font-semibold text-gray-900" data-id="element-567">
                          {bill.vendor_name}
                        </h3>
                        {isAllocatedBill && <Badge variant="info" data-id="element-568">Allocated</Badge>}
                        {bill.paid ? <Badge variant="success" data-id="element-569">Paid</Badge> : overdue ? <Badge variant="danger" data-id="element-570">Overdue</Badge> : <Badge variant="warning" data-id="element-571">Due</Badge>}
                      </div>
                      {bill.description && <p className="text-sm text-gray-600 mb-2" data-id="element-572">
                          {bill.description}
                        </p>}
                      <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-573">
                        <div className="flex items-center gap-1" data-id="element-574">
                          <CalendarIcon className="w-3 h-3" data-id="element-575" />
                          Due: {new Date(bill.due_date).toLocaleDateString()}
                        </div>
                        {bill.paid && bill.paid_date && <div className="flex items-center gap-1" data-id="element-576">
                            <CheckCircleIcon className="w-3 h-3 text-green-600" data-id="element-577" />
                            Paid:{' '}
                            {new Date(bill.paid_date).toLocaleDateString()}
                          </div>}
                      </div>
                      {bill.payment_notes && <div className="flex items-start gap-1 mt-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1" data-id="element-578">
                          <FileTextIcon className="w-3 h-3 mt-0.5 flex-shrink-0" data-id="element-579" />
                          <span data-id="element-580">{bill.payment_notes}</span>
                        </div>}
                    </div>
                    <div className="flex items-center gap-3 ml-4" data-id="element-581">
                      <div className="text-right" data-id="element-582">
                        <p className="text-lg font-bold text-gray-900" data-id="element-583">
                          {formatCurrency(bill.total_amount)}
                        </p>
                        {unallocated > 0 && !isAllocatedBill && <p className="text-xs text-orange-600" data-id="element-584">
                            {formatCurrency(unallocated)} unallocated
                          </p>}
                      </div>
                      <div className="flex items-center gap-1" data-id="element-585">
                        {!isAllocatedBill && <button onClick={() => handleEditBill(bill)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Edit bill" data-id="element-586">
                            <PencilIcon className="w-5 h-5" data-id="element-587" />
                          </button>}
                        {!bill.paid ? <button onClick={() => handleMarkPaidClick(bill.id)} className="p-2 text-gray-400 hover:text-green-600 transition-colors" title="Mark as paid" data-id="element-588">
                            <CheckCircleIcon className="w-5 h-5" data-id="element-589" />
                          </button> : <button onClick={() => markBillUnpaid(bill.id)} className="p-2 text-green-600 hover:text-gray-400 transition-colors" title="Mark as unpaid" data-id="element-590">
                            <XCircleIcon className="w-5 h-5" data-id="element-591" />
                          </button>}
                        <button onClick={() => deleteBill(bill.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete" data-id="element-592">
                          <TrashIcon className="w-5 h-5" data-id="element-593" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Allocations */}
                  {bill.allocations.length > 0 && <div className="border-t border-gray-100 pt-3 mt-3" data-id="element-594">
                      <p className="text-xs font-medium text-gray-600 mb-2" data-id="element-595">
                        Allocated to:
                      </p>
                      <div className="space-y-1" data-id="element-596">
                        {bill.allocations.map(allocation => <div key={allocation.id} className="flex items-center justify-between text-sm" data-id="element-597">
                            <div className="flex items-center gap-2" data-id="element-598">
                              <BuildingIcon className="w-3 h-3 text-gray-400" data-id="element-599" />
                              <span className="text-gray-700" data-id="element-600">
                                {allocation.company_name}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900" data-id="element-601">
                              {formatCurrency(allocation.amount)}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </Card>;
      })}
        </div>}
    </div>;
}